import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { executionApi } from '../services/api';
import { 
  WorkflowExecution, 
  ExecutionCreate, 
  ExecutionSummary 
} from '../types/workflow';
import { toast } from 'sonner';

// Query Keys
export const executionKeys = {
  all: ['executions'] as const,
  lists: () => [...executionKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...executionKeys.lists(), { filters }] as const,
  details: () => [...executionKeys.all, 'detail'] as const,
  detail: (id: string) => [...executionKeys.details(), id] as const,
  logs: (id: string) => [...executionKeys.detail(id), 'logs'] as const,
};

// Get all executions with pagination
export function useExecutions(limit: number = 50, offset: number = 0) {
  return useQuery({
    queryKey: executionKeys.list({ limit, offset }),
    queryFn: () => executionApi.getExecutions(limit, offset),
    staleTime: 30 * 1000, // 30 seconds - executions change frequently
  });
}

// Get execution by ID
export function useExecution(executionId: string | undefined) {
  return useQuery({
    queryKey: executionKeys.detail(executionId || ''),
    queryFn: () => executionApi.getExecution(executionId!),
    enabled: !!executionId,
    refetchInterval: (query) => {
      // Keep polling if execution is still running
      const data = query.state.data;
      return data?.status === 'running' ? 2000 : false;
    },
    staleTime: 5 * 1000, // 5 seconds
  });
}

// Get execution logs
export function useExecutionLogs(executionId: string | undefined) {
  return useQuery({
    queryKey: executionKeys.logs(executionId || ''),
    queryFn: () => executionApi.getExecutionLogs(executionId!),
    enabled: !!executionId,
    refetchInterval: () => {
      // Keep polling for logs if execution exists and might be running
      return executionId ? 5000 : false; // Refresh logs every 5 seconds
    },
    staleTime: 2 * 1000, // 2 seconds
  });
}

// Create execution mutation
export function useCreateExecution() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (execution: ExecutionCreate) => executionApi.createExecution(execution),
    onSuccess: (newExecution) => {
      // Add the new execution to the cache
      queryClient.setQueryData(
        executionKeys.detail(newExecution.execution_id),
        newExecution
      );
      
      // Invalidate executions list to show the new execution
      queryClient.invalidateQueries({ queryKey: executionKeys.lists() });
      
      toast.success('Workflow execution started');
    },
    onError: (error: Error) => {
      toast.error(`Failed to start execution: ${error.message}`);
    },
  });
}

// Cancel execution mutation
export function useCancelExecution() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (executionId: string) => executionApi.cancelExecution(executionId),
    onSuccess: (cancelledExecution) => {
      // Update the execution in the cache
      queryClient.setQueryData(
        executionKeys.detail(cancelledExecution.execution_id),
        cancelledExecution
      );
      
      // Invalidate executions list
      queryClient.invalidateQueries({ queryKey: executionKeys.lists() });
      
      toast.success('Execution cancelled');
    },
    onError: (error: Error) => {
      toast.error(`Failed to cancel execution: ${error.message}`);
    },
  });
}

// Hook for real-time execution updates
export function useExecutionUpdates(_executionId: string | undefined) {
  const queryClient = useQueryClient();

  // This would integrate with WebSocket for real-time updates
  // For now, we'll use polling via the useExecution hook
  
  const updateExecution = (updatedExecution: WorkflowExecution) => {
    queryClient.setQueryData(
      executionKeys.detail(updatedExecution.execution_id),
      updatedExecution
    );
    
    // Update the execution in any lists
    queryClient.setQueriesData(
      { queryKey: executionKeys.lists() },
      (oldData: any) => {
        if (oldData?.items) {
          return {
            ...oldData,
            items: oldData.items.map((item: ExecutionSummary) =>
              item.execution_id === updatedExecution.execution_id
                ? {
                    ...item,
                    status: updatedExecution.status,
                    steps_completed: updatedExecution.current_step,
                    completed_at: updatedExecution.completed_at,
                  }
                : item
            ),
          };
        }
        return oldData;
      }
    );
  };

  return { updateExecution };
}